import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {PipelineFilterConditionType, PipelinesFilterService} from "../pipelines-filter.service";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {PipelinesService} from "../../../pipelines.service";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {combineLatest, Observable, of, startWith, Subscription} from "rxjs";
import {map} from "rxjs/operators";
import Organization from "../../../../interfaces/organization";
import Sponsor from "../../../../interfaces/sponsor";
import {Person} from "../../../../interfaces/person";

@Component({
  selector: 'pipelines-condition-block',
  standalone: false,
  templateUrl: './pipelines-condition-block.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pipelines-condition-block.component.scss',
})
export class PipelinesConditionBlockComponent implements OnInit {
  @Input() index: number;
  @Output() removeBlock:EventEmitter<number> = new EventEmitter<number>()

  public conditionType: PipelineFilterConditionType = PipelineFilterConditionType.AND

  public toggleControl: FormControl = new FormControl()
  public conditionGroups: FormGroup[] = []
  public conditionFilterForm: FormGroup = new FormGroup({
    field: new FormControl(),
    fieldValue: new FormControl(),
    condition: new FormControl(),
    value: new FormControl(),
    valueTo: new FormControl(),
    conditionType: new FormControl(),
    children: new FormArray<FormGroup>([
      new FormGroup({
        field: new FormControl(),
        fieldValue: new FormControl(),
        condition: new FormControl(),
        value: new FormControl(),
        valueTo: new FormControl(),
        conditionType: new FormControl(),
      })
    ])
  })

  public typeAheadResults: Map<FormGroup, Observable<any[]>> = new Map()
  public sortGroups:FormGroup[] = []
  private filteredFieldsCache = new Map<any, Observable<any[]>>();
  constructor(public pipeFilterService: PipelinesFilterService, public pipelineService: PipelinesService) {
  }

  ngOnInit() {
    this.toggleControl.setValue(this.conditionType)
    this.toggleControl.valueChanges.subscribe((result) => this.conditionType = result)
    this._setFormGroups()
  }

  private _setFormGroups(){
    this.conditionGroups.push(this.conditionFilterForm)
  }

  public addInternalCondition(){
    if(!this._validateCompletedForms()){
      return;
    }
    let newFormGroup: FormGroup = new FormGroup({
      field: new FormControl(),
      fieldValue: new FormControl(),
      condition: new FormControl(),
      value: new FormControl(),
      valueTo: new FormControl(),
      conditionType: this.conditionGroups.length==0 ? new FormControl()
      : new FormControl(this.toggleControl.value),
      children: new FormArray<FormGroup>([])
    })
    this.conditionGroups.push(newFormGroup)
  }

  public addInternalSubCondition(group: FormGroup){
    if(!this._validateCompletedChildForms(group)){
      return;
    }
    let children: FormArray = group.get('children') as FormArray
    children.push(new FormGroup({
      field: new FormControl(),
      fieldValue: new FormControl(),
      condition: new FormControl(),
      value: new FormControl(),
      valueTo: new FormControl(),
      conditionType: new FormControl(this.toggleControl.value)
    }))

  }


  private _validateCompletedChildForms(form: FormGroup){
    const children: FormArray = form.get('children') as FormArray
    if (!children || children.length === 0) {
      return true;
    }
    return children.controls.every(child =>
      Object.entries((child as FormGroup).controls)
        .filter(([key]) => key !== 'conditionType' && key !== 'valueTo' && key !== 'field')
        .every(([, control]) => {
          const condition = (child as FormGroup).get('condition')?.value?.name ?? null
          if(condition === 'Is Empty' || condition === 'Is Not Empty'){
            return true;
          }
          return control.value !== null && control.value !== ''
        })
    );
  }

  private _validateCompletedForms(){
    return this.conditionGroups.every(group => {
      const children: FormArray = group.get('children') as FormArray;
      const hasFilledChildren = children && children.length > 0 &&
        children.controls.some(child => {
          const conditionCtrl = (child as FormGroup).get('condition');
          return conditionCtrl?.value !== null && conditionCtrl?.value !== '';
        });

      if (hasFilledChildren) {
        return this._validateCompletedChildForms(group);
      }

      return Object.entries(group.controls)
        .filter(([key]: [string, unknown]) => key !== 'conditionType' && key !== 'valueTo' && key !== 'fieldValue' && key !== 'children')
        .every(([, control]) => {
          const condition = group.get('condition')?.value
          if (condition === 'Empty' || condition === 'Not Empty') {
            return true
          }
          return control.value !== null && control.value !== '';
        });
    });
  }

  public removeCondition(condition: FormGroup){
    this.conditionGroups = this.conditionGroups.filter((group:FormGroup) => group !== condition)
  }
  public removeSubCondition(condition: FormGroup, form: FormGroup){
    const children: FormArray = condition.get('children') as FormArray
    const index = children.controls.indexOf(form)
    if(index > -1){
      children.removeAt(index)
    }
    if(index == 0){
      condition.controls['field'].reset()
    }
  }

  public removeConditionBlock(){
    this.removeBlock.emit(this.index)
  }

  public onConditionChange(condition: FormGroup, formControl?: string){
    let control:string = formControl ? formControl : 'field'
    let field: FormControl = condition.get(control) as FormControl
    if((field.value.format=='id' || field.value.format=='json' || field.value.format=='select' || field.value.format=='string') && field.value.model!==null){
      if(field.value.model==='Participant'){
        return;
      }
      this.pipelineService.getModelLookup(field.value.model, field.value.field)
    }
  }

  public resolveFromSaved(conditions: any[],fields: any[], blockType?: string){
    this.conditionGroups = [];

    const mapConditionType = (type:string): PipelineFilterConditionType => {
      return type === 'OR' ? PipelineFilterConditionType.OR : PipelineFilterConditionType.AND
    }

    if(blockType){
      const resolved = mapConditionType(blockType)

      setTimeout(() => {
        this.toggleControl.setValue(resolved, { emitEvent: false });
        this.conditionType = resolved;
        this.conditionGroups.forEach((group:FormGroup) => {
          group.get('conditionType')?.setValue(this.toggleControl.value, {emitEvent:false})
        })
      });
    }
    const _buildSpecialLookupGroup = (entry:any, fieldDef:any) => {
      const parts: any[] = Array.isArray(entry.value) ? entry.value : [];
      const valuePart = parts[1] ?? parts[0] ?? {};

      const conditionObj = this.pipeFilterService
          .conditionFromAbbrv('select', valuePart.filter_type ?? 'EQ')
        ?? this.pipeFilterService.conditionFromAbbrv('int', valuePart.filter_type ?? 'EQ');

      return new FormGroup({
        field: new FormControl(fieldDef),
        fieldValue: new FormControl(),
        condition: new FormControl(conditionObj),
        value: new FormControl(),
        valueTo: new FormControl(),
        conditionType: new FormControl(mapConditionType(entry.filter_type)),
        children: new FormArray<FormGroup>([])
      });
    }

    const _buildStandardGroup = (entry:any, fieldDef:any) => {
      const conditionObj = this.pipeFilterService
        .conditionFromAbbrv(fieldDef.format, entry.filter_type);

      let raw = entry.value;
      if (conditionObj?.abbrv === 'LIKE' && typeof raw === 'string') {
        raw = raw.replace(/^%/, '').replace(/%$/, '');
      }

      let value: any = raw;
      let valueTo: any = null;
      if (fieldDef.format === 'boolean') {
        value = { name: raw === true || raw === 'true' ? 'Yes' : 'No' };
      } else if (Array.isArray(raw) && conditionObj?.name === 'Is Between') {
        value = raw[0];
        valueTo = raw[1];
      }
      if(fieldDef.format=='date'){
        value = value + 'T00:00:00'
        valueTo = valueTo + 'T00:00:00'
      }

      return new FormGroup({
        field: new FormControl(fieldDef),
        fieldValue: new FormControl(),
        condition: new FormControl(conditionObj),
        value: new FormControl(value),
        valueTo: new FormControl(valueTo),
        conditionType: new FormControl(mapConditionType(entry.filter_type)),
        children: new FormArray<FormGroup>([])
      });
    }

    const _buildJsonGroup = (entry:any, fieldDef:any) => {
      const childDefs: any[] = fieldDef.children ?? [];
      const childEntries: any[] = Array.isArray(entry.value) ? entry.value : [];

      const children = childEntries.map((c: any) => {
        let effField = c.field;
        let effValue = c.value;
        let effType = c.filter_type;
        if (Array.isArray(c.value) && c.value[0] && c.value[0].field) {
          const inner = c.value[0];
          effField = c.field;
          effValue = inner.value;
          effType = inner.filter_type;
        }
        const childDef = childDefs.find((cf: any) =>
          cf.field === effField || cf.id_field === effField) ?? null;
        const conditionObj = childDef
          ? this.pipeFilterService.conditionFromAbbrv(childDef.format, effType)
          : null;

        let raw = effValue;
        if (conditionObj?.abbrv === 'LIKE' && typeof raw === 'string') {
          raw = raw.replace(/^%/, '').replace(/%$/, '');
        }
        let toVal: string | null = null
        if(effField=='date'){
          if(conditionObj?.abbrv === 'BTW'){
            let orgRaw = raw
            raw = orgRaw[0] + 'T00:00:00'
            toVal = orgRaw[1] + 'T00:00:00'
          } else {
            raw = raw + 'T00:00:00'
          }
        }

        return new FormGroup({
          field: new FormControl(),
          fieldValue: new FormControl(childDef),
          condition: new FormControl(conditionObj),
          value: new FormControl(raw),
          valueTo: new FormControl(toVal),
          conditionType: new FormControl(mapConditionType(effType))
        });
      });


      return new FormGroup({
        field: new FormControl(fieldDef),
        fieldValue: new FormControl(),
        condition: new FormControl(),
        value: new FormControl(),
        valueTo: new FormControl(),
        conditionType: new FormControl(mapConditionType(entry.filter_type)),
        children: new FormArray<FormGroup>(children)
      });
    }
    (conditions ?? []).forEach((entry: any) => {
      const fieldDef = fields.find((f: any) => f.field === entry.field || f.id_field === entry.field) ?? null;
      if(!fieldDef){
        return;
      }
      let group: FormGroup
      let isPipelineDates: boolean = fieldDef.name === 'Pipeline Dates'
      if (fieldDef.format === 'json' && fieldDef.special_lookup) {
        group = _buildSpecialLookupGroup(entry, fieldDef);
      } else if (fieldDef.format === 'json' && !fieldDef.special_lookup) {
        group = _buildJsonGroup(entry, fieldDef)
      } else {
        group = _buildStandardGroup(entry, fieldDef)
      }
      this.conditionGroups.push(group)

      if(fieldDef.model){
        if(entry?.filter_type!=='LIKE'){
          this.onConditionChange(group)
          this._remapOnSelectionReady(group, fieldDef, entry)
        }
      }
      if(fieldDef.format==='json' && !fieldDef.special_lookup){
        this._remapJsonChildren(group, fieldDef, entry)
      }
      if (this.conditionGroups.length===0){
        this._setFormGroups()
      }
    })
    this.conditionGroups.forEach((group:FormGroup) => {
      group.get('conditionType')?.setValue(this.toggleControl.value, {emitEvent:false})
    })
  }

  private _remapJsonChildren(group: FormGroup, fieldDef: any, entry: any) {
    const childDefs: any[] = fieldDef.children ?? [];
    const childEntries: any[] = Array.isArray(entry.value) ? entry.value : [];
    const children = group.get('children') as FormArray;


    childEntries.forEach((c: any, i: number) => {
      const childForm = children.at(i) as FormGroup;
      if (!childForm) { return; }

      let effField = c.field;
      let raw = c.value;

      if (Array.isArray(c.value) && c.value[0] && c.value[0].field) {
        raw = c.value[0].value;
      }

      const childDef = childDefs.find((cf: any) =>
        cf.field === effField|| cf.id_field === effField) ?? null;
      // ... inside the childEntries.forEach loop

      if(c.filter_type=='LIKE'){
        childForm.get("value")?.setValue(raw.replaceAll("%",""))
        return
      }

      if(childDef.format==='boolean'){
        let selections = this.pipeFilterService.getConditionSelectionByType('boolean')

        let val = selections.find((sel:any)=> sel?.name===raw)
        childForm.get("value")?.setValue(val ?? '')
        return;
      }


      // Only object-backed selects need remapping; plain inputs keep their raw value.
      if (!childDef?.model || (childDef.format !== 'select' && !childDef.is_typeahead)) { return; }
      // Trigger the lookup for this child's model.
      if(childDef.model=='Participant'){
        this.pipelineService.callSingleParticipantRow(raw).subscribe((result:any) => {
          let data = result.data.participantById
          let user: Organization | Person | Sponsor = data.person !== null ? data.person : data.organization !== null ? data.organization : data.sponsor
          childForm.get('value')?.setValue(user)
        })
      } else {
        this.pipelineService.getModelLookup(childDef.model, childDef.field);
      }

      // const raw = c.value;
      const isIdField = !!childDef.id_field;
      const matchKey = (opt: any, val: any) => {
        const valStr = String(val);
        if (isIdField) {
          return String(opt.id) === valStr || String(opt.index) === valStr;
        }
        return opt.name === val || String(opt.index) === valStr;
      };

      const valueControl = childForm.get('value');
      if(childDef.model!=='Participant'){
        valueControl?.disable({ emitEvent: false })
        valueControl?.setValue("Loading selection...")
      }
      const sub = new Subscription();
      sub.add(
        this.pipelineService.modelSelections$.subscribe((modelMap) => {

          if (!modelMap.has(childDef.model) || childDef.model=='Participant') { return; }


          const isMulti = this.pipeFilterService.isMultiSelect(childForm, 'fieldValue');
          const options = modelMap.get(childDef.model) || [];
          if (!options.length) { return; }

          if (Array.isArray(raw)) {
            const matched = raw
              .map((v: any) => options.find((opt: any) => matchKey(opt, v)))
              .filter(Boolean);

            if (matched.length > 0) {
              childForm.get('value')?.setValue(isMulti ? matched : (matched[0] ?? null));
            }
          } else {
            const matched = options.find((opt: any) => matchKey(opt, raw));
            if (matched) {
              childForm.get('value')?.setValue(isMulti ? [matched] : matched);

              if (childDef.is_typeahead) {
                this.typeAheadResults.set(childForm, of([matched]));
              }
            }
          }
          valueControl?.enable({emitEvent:false})
          sub.unsubscribe();
        })
      )
      // const sub = new Subscription();
      // sub.add(
      //   this.pipelineService.modelSelections$.subscribe((modelMap) => {
      //     console.log(modelMap)
      //     console.log(childDef)
      //     if (!modelMap.has(childDef.model)) { return; }
      //
      //     const isMulti = this.pipeFilterService.isMultiSelect(childForm, 'fieldValue');
      //     const options = isMulti
      //       ? this.pipelineService.getModelSelections(childForm, 'fieldValue')
      //       : this.pipelineService.getSubSelections(childForm);
      //     if (!options || !options.length) { return; }
      //
      //     if (Array.isArray(raw)) {
      //       const matched = raw
      //         .map((v: any) => options.find((opt: any) => matchKey(opt, v)))
      //         .filter(Boolean);
      //       childForm.get('value')?.setValue(isMulti ? matched : (matched[0] ?? null));
      //     } else {
      //       const matched = options.find((opt: any) => matchKey(opt, raw));
      //       if (matched) {
      //         childForm.get('value')?.setValue(isMulti ? [matched] : matched);
      //       }
      //     }
      //     console.log(childForm)
      //     sub.unsubscribe();
      //   })
      // );
    });
  }

  private _remapOnSelectionReady(group: FormGroup, fieldDef: any, entry: any) {

    const _remapSpecialLookup = (group: FormGroup, entry: any, options: any[]) => {
      const parts: any[] = Array.isArray(entry.value) ? entry.value : [];
      const typeVal = parts[0]?.value ?? null;
      const valueVals: any[] = Array.isArray(parts[1]?.value)
        ? parts[1].value
        : parts[1]?.value != null ? [parts[1].value] : [];

      const fieldValueOpt = options.find((opt: any) =>
        String(opt.id) === String(typeVal) || opt.name === typeVal);
      if (fieldValueOpt) {
        group.get('fieldValue')?.setValue(fieldValueOpt);
      }

      const subOptions = this.pipelineService.getSubSelections(group);
      const matched = valueVals
        .map((v: any) => subOptions.find((opt: any) =>
          String(opt.id) === String(v) || opt.name === v))
        .filter(Boolean);

      // Hacky Pipeline Dates exception
      if(group.get('field')?.value?.name === 'Pipeline Dates'){
        let state = entry.value.filter((val:any) =>val.field=='date')
        let date1 = state[0].value[0]
        let date2 = state[0].value.length>1 ? state[0].value[1] : null
        group.get('value')?.setValue(date1 + 'T00:00:00')
        group.get('valueTo')?.setValue(date2 + 'T00:00:00')
      } else {
        group.get('value')?.setValue(matched);

      }
    }

    const _remapStandardSelect = (group: FormGroup, entry: any, options: any[]) => {
      const condition = group.get('condition')?.value;
      if (condition?.name === 'Is Empty' || condition?.name === 'Is Not Empty') {
        return;
      }

      const raw = entry.value;
      const isIdField = !!group.get('field')?.value?.id_field;

      const matchKey = (opt: any, val: any) => {
        const valStr = String(val);
        if (isIdField) {
          return String(opt.id) === valStr || String(opt.index) === valStr;
        }
        return opt.name === val || String(opt.index) === valStr;
      };
      if (condition?.name === 'Is Between' && Array.isArray(raw)) {
        const from = options.find((opt: any) => matchKey(opt, raw[0])) ?? null;
        const to = options.find((opt: any) => matchKey(opt, raw[1])) ?? null;
        group.get('value')?.setValue(from);
        group.get('valueTo')?.setValue(to);
        return;
      }

      if (Array.isArray(raw)) {
        const matched = raw
          .map((v: any) => options.find((opt: any) => matchKey(opt, v)))
          .filter(Boolean);
        group.get('value')?.setValue(matched);
      } else {
        const matched = options.find((opt: any) => matchKey(opt, raw));
        if (matched) {
          group.get('value')?.setValue(
            this.pipeFilterService.isMultiSelect(group) ? [matched] : matched);
        }
      }
    }
    const valueControl = group.get('value');
    valueControl?.disable({ emitEvent: false })
    valueControl?.setValue("Loading selection...")

    const sub = new Subscription();
    sub.add(
      this.pipelineService.modelSelections$.subscribe((modelMap) => {
        // 1. Explicitly check if the model map has data for THIS specific field's model yet
        const currentModel = fieldDef.model;
        if (!currentModel || !modelMap.has(currentModel)) {
          return;
        }

        const options = this.pipelineService.getModelSelections(group);
        if (!options || !options.length) {
          return;
        }
        // 2. Perform the remapping now that we know options exist for our model
        if (fieldDef.format === 'json' && fieldDef.special_lookup) {
          _remapSpecialLookup(group, entry, options);
        } else {
          _remapStandardSelect(group, entry, options);
        }

        // 3. Unsubscribe safely now that mapping for this block is complete
        valueControl?.enable({ emitEvent: false })
        sub.unsubscribe();
      })
    );
  }

  public syncConditionToggle(){
    if(this.conditionGroups.length > 0){
      const firstType = this.conditionGroups[0].get('conditionType')?.value
      if(firstType !== undefined && firstType !== null){
        this.toggleControl.setValue(firstType, {emitEvent: false})
        this.conditionType = firstType
      }
    }
  }

  public compareOptions = (a: any, b: any): boolean => {
    if (a == null || b == null) {
      return a === b;
    }
    if (a.id != null && b.id != null) {
      return String(a.id) === String(b.id);
    }
    if (a.index != null && b.index != null) {
      return String(a.index) === String(b.index);
    }
    return a === b || a.name === b.name;
  }

  public onValueChange(event:any, condition: FormGroup, control:string){
    let string = event.target?.value ?? ''
    let model = condition.controls[control].value.model
    let field = condition.controls[control].value.field
    if(string!==''){
      this.pipelineService.searchModel(model, string).subscribe((results) => {
        this.typeAheadResults.set(condition, results instanceof Observable ? results : of(results ?? []))
      })
    } else {
      this.typeAheadResults.set(condition, of([]))
    }
  }

  filteredFields(condition: any) {
    const cached = this.filteredFieldsCache.get(condition)
    if(cached){
      return cached
    }
    const control = this.pipeFilterService.getControl(condition, 'field');

    const streamResults$ = combineLatest([
      this.pipelineService.fieldFilters$,
      control.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([fields, value]) => {
        const search =
          typeof value === 'string'
            ? value.toLowerCase()
            : value?.name?.toLowerCase() ?? '';
        if(!search){
          return fields;
        }
        return fields.filter(f =>
          f.name.toLowerCase().includes(search)
        );
      })
    );
    this.filteredFieldsCache.set(condition, streamResults$)
    return streamResults$
  }

  public onFieldSelected(event: MatAutocompleteSelectedEvent, condition: FormGroup) {
    const selectedField = event.option.value;

    const fieldControl = this.pipeFilterService.getControl(condition, 'field');
    fieldControl.setValue(selectedField, { emitEvent: true });

    const childrenArray = condition.get('children') as FormArray;
    if (selectedField && selectedField.children && Array.isArray(selectedField.children)) {
      while (childrenArray.length > 0) {
        childrenArray.removeAt(0);
      }

      if (childrenArray.length === 0 && selectedField.children.length > 0) {
        childrenArray.push(new FormGroup({
          field: new FormControl(),
          fieldValue: new FormControl(),
          condition: new FormControl(),
          value: new FormControl(),
          valueTo: new FormControl(),
          conditionType: new FormControl(this.toggleControl.value)
        }));
      }
    }
    this.onConditionChange(condition);
  }


  displayFn(field: any) {
    return field ? field.name : '';
  }


  public getTypeAheadResults(condition: FormGroup) : Observable<any[]>{
    return this.typeAheadResults.get(condition) ?? of([])
  }

  public clearConditionalData(){
    this.conditionGroups = []
    this.conditionFilterForm.reset()
    this.conditionGroups.push(this.conditionFilterForm)
  }

  public displayVal(value:any): string {
    return value?.name ?? value ?? ''
  }


  protected readonly PipelineFilterConditionType = PipelineFilterConditionType;
  protected readonly FormArray = FormArray;
  protected readonly JSON = JSON;
}
